import { FC } from "react";
import { Box, Button, FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import { Formik } from "formik";
import { AppModal } from "../../../../components/ui";
import { TutorialModel } from "../../../../models/TutorialModel";
import { parseDuration } from "../../../../utils/duration";
import { TUTORIAL_CATEGORIES } from "../use-tutorial-admin";

type Props = {
    open: boolean;
    onClose: () => void;
    isEditing: boolean;
    btnSaveDisabled: boolean;
    formik: any;
    tutorialViewModel: TutorialModel;
    setTutorialViewModel: (model: TutorialModel) => void;
    durationText: string;
    setDurationText: (value: string) => void;
    productViewInitial: TutorialModel;
    productSchema: any;
};

/** Modal de cadastro/edição de tutorial. */
const TutorialFormModal: FC<Props> = ({
    open,
    onClose,
    isEditing,
    btnSaveDisabled,
    formik,
    tutorialViewModel,
    setTutorialViewModel,
    durationText,
    setDurationText,
    productViewInitial,
    productSchema,
}) => {
    const { errors, touched, handleSubmit, getFieldProps } = formik;

    return (
        <AppModal
            open={open}
            onClose={onClose}
            kicker={isEditing ? "Editar" : "Cadastrar"}
            title={isEditing ? "Editar tutorial" : "Novo tutorial"}
            maxWidth={640}
            footer={
                <>
                    <Button variant="outlined" color="secondary" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" disabled={btnSaveDisabled} onClick={() => handleSubmit()}>
                        Salvar
                    </Button>
                </>
            }
        >
            <Formik initialValues={productViewInitial} validationSchema={productSchema} onSubmit={() => {}}>
                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        label="Título"
                        {...getFieldProps("title")}
                        value={tutorialViewModel.title}
                        variant="outlined"
                        fullWidth
                        onChange={(event) => {
                            tutorialViewModel.title = event.target.value;
                            setTutorialViewModel({ ...tutorialViewModel });
                        }}
                        error={Boolean(touched.title && errors.title)}
                        helperText={touched.title && errors.title}
                    />

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            select
                            label="Categoria"
                            fullWidth
                            value={tutorialViewModel.category ?? "Primeiros passos"}
                            onChange={(event) => {
                                tutorialViewModel.category = event.target.value;
                                setTutorialViewModel({ ...tutorialViewModel });
                            }}
                        >
                            {TUTORIAL_CATEGORIES.map((cat) => (
                                <MenuItem key={cat} value={cat}>
                                    {cat}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Duração"
                            placeholder="0:00"
                            fullWidth
                            value={durationText}
                            onChange={(event) => {
                                const value = event.target.value;
                                setDurationText(value);
                                tutorialViewModel.durationSeconds = parseDuration(value);
                                setTutorialViewModel({ ...tutorialViewModel });
                            }}
                        />
                    </Box>

                    <TextField
                        label="URL do video do Youtube"
                        {...getFieldProps("url")}
                        value={tutorialViewModel.url}
                        variant="outlined"
                        fullWidth
                        onChange={(event) => {
                            const url = event.target.value;
                            const videoId: string | undefined = url.split("v=")[1]?.split("&")[0];
                            tutorialViewModel.url = videoId;
                            setTutorialViewModel({ ...tutorialViewModel });
                        }}
                        error={Boolean(touched.url && errors.url)}
                        helperText={touched.url && errors.url}
                    />

                    <TextField
                        label="Descrição do subtítulo"
                        {...getFieldProps("subtitle")}
                        multiline
                        fullWidth
                        rows={4}
                        value={tutorialViewModel.subtitle}
                        variant="outlined"
                        onChange={(event) => {
                            tutorialViewModel.subtitle = event.target.value;
                            setTutorialViewModel({ ...tutorialViewModel });
                        }}
                        error={Boolean(touched.subtitle && errors.subtitle)}
                        helperText={touched.subtitle && errors.subtitle}
                    />

                    <TextField
                        label="Descrição do texto secundário"
                        {...getFieldProps("description")}
                        multiline
                        fullWidth
                        rows={4}
                        value={tutorialViewModel.description}
                        variant="outlined"
                        onChange={(event) => {
                            tutorialViewModel.description = event.target.value;
                            setTutorialViewModel({ ...tutorialViewModel });
                        }}
                        error={Boolean(touched.description && errors.description)}
                        helperText={touched.description && errors.description}
                    />

                    <FormControlLabel
                        label="Mostrar ícone de 'Novo'?"
                        control={
                            <Switch
                                checked={Boolean(tutorialViewModel.new_icon)}
                                onChange={(event) => {
                                    tutorialViewModel.new_icon = event.target.checked;
                                    setTutorialViewModel({ ...tutorialViewModel });
                                }}
                                name="new_icon"
                            />
                        }
                    />
                </Box>
            </Formik>
        </AppModal>
    );
};

export default TutorialFormModal;
